import React, { useState, useEffect } from 'react';
import { vacancyService, companyService, coordinatorService, placementDriveService } from '../../services/api';
import './AdminPage.css';

function AdminVacancies() {
  const [vacancies, setVacancies] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [coordinators, setCoordinators] = useState([]);
  const [placementDrives, setPlacementDrives] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingVacancy, setEditingVacancy] = useState(null);
  const [formData, setFormData] = useState({
    Title: '',
    Description: '',
    Conditions: '',
    Question1: '',
    Question2: '',
    Question3: '',
    LastDate: '',
    CompanyId: '',
    CordinatorId: '',
    PlacementDriveId: ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadData();
    loadDropdowns();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const data = await vacancyService.getVacancies();
      setVacancies(data);
    } catch (error) {
      alert('Error loading vacancies: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadDropdowns = async () => {
    try {
      const [companiesData, coordinatorsData, placementDrivesData] = await Promise.all([
        companyService.getCompanies(),
        coordinatorService.getCoordinators(),
        placementDriveService.getPlacementDrives()
      ]);
      setCompanies(companiesData);
      setCoordinators(coordinatorsData);
      setPlacementDrives(placementDrivesData);
    } catch (error) {
      console.error('Error loading dropdowns:', error);
    }
  };

  const handleAdd = () => {
    setEditingVacancy(null);
    setFormData({
      Title: '',
      Description: '',
      Conditions: '',
      Question1: '',
      Question2: '',
      Question3: '',
      LastDate: '',
      CompanyId: '',
      CordinatorId: '',
      PlacementDriveId: ''
    });
    setIsModalOpen(true);
  };

  const handleEdit = (vacancy) => {
    setEditingVacancy(vacancy);
    setFormData({
      Id: vacancy.Id,
      Title: vacancy.Title || '',
      Description: vacancy.Description || '',
      Conditions: vacancy.Conditions || '',
      Question1: vacancy.Question1 || '',
      Question2: vacancy.Question2 || '',
      Question3: vacancy.Question3 || '',
      LastDate: vacancy.LastDate ? vacancy.LastDate.split('T')[0] : '',
      CompanyId: vacancy.CompanyId || '',
      CordinatorId: vacancy.CordinatorId || '',
      PlacementDriveId: vacancy.PlacementDriveId || ''
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this vacancy?')) {
      try {
        await vacancyService.deleteVacancy(id);
        loadData();
      } catch (error) {
        alert('Error deleting vacancy: ' + error.message);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      await vacancyService.saveVacancy(formData);
      setIsModalOpen(false);
      loadData();
    } catch (error) {
      alert('Error saving vacancy: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-page">
      <div className="page-header page-headeradmin">
        <h1>Vacancies Management</h1>
        <button className="btn-primary" onClick={handleAdd}>Add Vacancy</button>
      </div>

      {loading && !vacancies.length ? (
        <div className="loading">Loading...</div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Company</th>
                <th>Placement Drive</th>
                <th>Last Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {vacancies.map((vacancy) => (
                <tr key={vacancy.Id}>
                  <td>{vacancy.Title}</td>
                  <td>{vacancy.Company?.Name}</td>
                  <td>{vacancy.PlacementDrive?.Title}</td>
                  <td>{vacancy.LastDate ? new Date(vacancy.LastDate).toLocaleDateString() : ''}</td>
                  <td>
                    <button className="btn-edit" onClick={() => handleEdit(vacancy)}>Edit</button>
                    <button className="btn-delete" onClick={() => handleDelete(vacancy.Id)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {isModalOpen && (
        <div className="modal-overlay" onClick={() => setIsModalOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingVacancy ? 'Edit Vacancy' : 'Add Vacancy'}</h2>
              <button className="modal-close" onClick={() => setIsModalOpen(false)}>×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-grid">
                <div className="form-group full-width">
                  <label>Title *</label>
                  <input
                    type="text"
                    value={formData.Title}
                    onChange={(e) => setFormData({ ...formData, Title: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group full-width">
                  <label>Description</label>
                  <textarea
                    value={formData.Description}
                    onChange={(e) => setFormData({ ...formData, Description: e.target.value })}
                    rows="3"
                  />
                </div>
                <div className="form-group full-width">
                  <label>Conditions</label>
                  <textarea
                    value={formData.Conditions}
                    onChange={(e) => setFormData({ ...formData, Conditions: e.target.value })}
                    rows="3"
                  />
                </div>
                <div className="form-group">
                  <label>Last Date</label>
                  <input
                    type="date"
                    value={formData.LastDate}
                    onChange={(e) => setFormData({ ...formData, LastDate: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Company</label>
                  <select
                    value={formData.CompanyId}
                    onChange={(e) => setFormData({ ...formData, CompanyId: e.target.value })}
                  >
                    <option value="">Select Company</option>
                    {companies.map((company) => (
                      <option key={company.Id} value={company.Id}>{company.Name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Coordinator</label>
                  <select
                    value={formData.CordinatorId}
                    onChange={(e) => setFormData({ ...formData, CordinatorId: e.target.value })}
                  >
                    <option value="">Select Coordinator</option>
                    {coordinators.map((coordinator) => (
                      <option key={coordinator.Id} value={coordinator.Id}>{coordinator.Name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Placement Drive</label>
                  <select
                    value={formData.PlacementDriveId}
                    onChange={(e) => setFormData({ ...formData, PlacementDriveId: e.target.value })}
                  >
                    <option value="">Select Placement Drive</option>
                    {placementDrives.map((drive) => (
                      <option key={drive.Id} value={drive.Id}>{drive.Title}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group full-width">
                  <label>Question 1</label>
                  <input
                    type="text"
                    value={formData.Question1}
                    onChange={(e) => setFormData({ ...formData, Question1: e.target.value })}
                  />
                </div>
                <div className="form-group full-width">
                  <label>Question 2</label>
                  <input
                    type="text"
                    value={formData.Question2}
                    onChange={(e) => setFormData({ ...formData, Question2: e.target.value })}
                  />
                </div>
                <div className="form-group full-width">
                  <label>Question 3</label>
                  <input
                    type="text"
                    value={formData.Question3}
                    onChange={(e) => setFormData({ ...formData, Question3: e.target.value })}
                  />
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setIsModalOpen(false)}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={loading}>
                  {loading ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminVacancies;

